import * as React from 'react';
import * as _ from 'underscore';

import { SpaceHeaderTestIds } from '../Header/SpaceHeader';
import { TextFieldWidget } from '@tableau/widgets';
import { Messages, Formatters } from '../../../codegen/Localize';
import { SpaceHeaderStyles } from '../Header/SpaceHeaderStyles';
import { CollectionSpaceHeaderProps } from './CollectionSpaceHeader';
import { CollectionsResourceQuery } from './Collections';
import { CollectionsErrorCode } from '../../../ts/helpers/ErrorCodes';
import { CommonStyles } from '../../../common/react/CommonStyles';
import { useInlineEditor, InlineEditorLogic, isReadonlyMode, InlineEditorLogic_EditMode, isEditMode } from '@tableau/vizportalclient-common-components';

export const COLLECTION_NAME_MAX_LENGTH = 80; // if this max changes, update `createCollectionNameTooLongError` string

export interface EditableCollectionNameComponentProps extends CollectionSpaceHeaderProps {
  readonly onEditChange: (isEditing: boolean) => void;
  readonly canEdit: boolean;
}

export function EditableCollectionName(props: EditableCollectionNameComponentProps) {
  const [errorMessage, setErrorMessage] = React.useState(undefined);
  const [name, setName] = React.useState(props.item.name);

  const updateErrorMessage = (newMessage: string): void => {
    setErrorMessage(newMessage);
  };

  const clearErrorMessage = (): void => {
    setErrorMessage(undefined);
  };

  const saveNameEdit = (luid: string, updateErrorMessage: (newMessage: string) => void) => {
    return async (newName: string): Promise<void> => {
      const updatedCollectionData = {
        luid,
        name: newName
      };

      try {
        await (new CollectionsResourceQuery()).update([updatedCollectionData]);
        props.reloadSpace();
        props.onEditChange && props.onEditChange(false);
        setName(newName);
        updateErrorMessage(undefined);
      } catch (e) {
        let errorText = Messages.collectionRenameError({
          COLLECTION_NAME: newName
        });
        if (e.data && e.data.errorCode === CollectionsErrorCode.DUPLICATE_KEY) {
          errorText = props.isOwner ? Messages.collectionOwnerDuplicateRenameError() : Messages.collectionNonOwnerDuplicateRenameError();
        }

        updateErrorMessage(errorText);
        throw e;
      }
    };
  };

  const canEdit = props.isAdminOrOwner && props.canEdit;

  const logic: InlineEditorLogic<string> = useInlineEditor<string>({
    editButtonLabel: Messages.editName(),
    editorStateWhenEnteringEditMode: name,
    canEdit: canEdit,
    onSave: saveNameEdit(props.item.luid, updateErrorMessage),
    onCancel: () => {
      props.onEditChange && props.onEditChange(false);
      setErrorMessage(undefined);
    },
    canSave: isValidName,
  });

  const editableStyling: string = canEdit ? SpaceHeaderStyles.HeaderEditableInlineElement : '';

  if (isReadonlyMode(logic)) {
    return <h1
      data-tb-test-id={SpaceHeaderTestIds.name}
      className={`${SpaceHeaderStyles.CollectionTitleReadonly} ${editableStyling}`}
      onClick={() => {
        canEdit && logic.enterEditMode();
        props.onEditChange && props.onEditChange(true);
      }}
    >
      {name}
      {props.isAdminOrOwner && <span style={{ paddingLeft: CommonStyles.Sizes.SmallSpace }}>{logic.editButton}</span>}
    </h1>;
  } else {
    return <CollectionInlineNameEditorEditMode editLogic={logic} errorMessage={errorMessage} clearErrorMessage={clearErrorMessage}/>;
  }
}

function isValidName(name: string): boolean {
  return (!_.isUndefined(name) &&
    name.trim().length > 0 &&
    name.length <= COLLECTION_NAME_MAX_LENGTH);
}

export interface CollectionInlineNameEdtitorEditModeProps {
  readonly editLogic: InlineEditorLogic_EditMode<string>;
  readonly errorMessage: string;
  readonly clearErrorMessage: () => void;
}

export function CollectionInlineNameEditorEditMode(props: CollectionInlineNameEdtitorEditModeProps): JSX.Element {
  const inputEl = React.useRef<HTMLInputElement>(null);
  const [isErrored, setIsErrored] = React.useState(undefined);

  const getMessageForNameTextInput = (value: string): string => {
    if (value.trim().length === 0) {
      !isErrored && setIsErrored(true);
      return Messages.createCollectionNameWhitespaceError();
    } else if (value.length > COLLECTION_NAME_MAX_LENGTH) {
      !isErrored && setIsErrored(true);
      return Messages.createCollectionNameTooLongError();
    } else {
      isErrored && setIsErrored(undefined);
      const remainingChars = COLLECTION_NAME_MAX_LENGTH - value.length;
      return Messages.remainingCharacters({
        COUNT: remainingChars,
        COUNT_LOC: Formatters.formatCount(remainingChars)
      });
    }
  };

  React.useEffect(() => {
    inputEl.current?.focus();
  }, []);

  const onNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newText = e.target.value.substring(0, COLLECTION_NAME_MAX_LENGTH);
    props.errorMessage && props.clearErrorMessage();
    props.editLogic.setEditorStateWithinEditMode(newText);
    setMessage(getMessageForNameTextInput(newText));
  };

  const [message, setMessage] = React.useState(props.errorMessage || getMessageForNameTextInput(props.editLogic.editorState));

  return <span data-tb-test-id={'collection-name-edit'} className={SpaceHeaderStyles.CollectionTitleEditContainer}>
    <TextFieldWidget
      testId='collection-name-edit'
      label={undefined}
      maxLength={COLLECTION_NAME_MAX_LENGTH}
      onChange={onNameChange}
      value={props.editLogic.editorState}
      className={`${SpaceHeaderStyles.ColumnFlexGrow} ${SpaceHeaderStyles.CollectionTitleInput}`}
      message={props.errorMessage || message}
      valid={(!!props.errorMessage || isErrored) ? false : undefined} // valid=undefined so textfield won't have green valid styling
      ref={inputEl}
    />
    <div className={SpaceHeaderStyles.ConfirmCancelButtons}>
      {props.editLogic.cancelButton} {props.editLogic.saveButton}
    </div>
  </span>;
}
